import { useState } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { ComplianceRequirement, ComplianceMetrics } from '../types/compliance';
import { sampleRequirements } from '../data/sampleCompliance';

export function useCompliance(dealId?: string) {
  const [requirements, setRequirements] = useLocalStorage<ComplianceRequirement[]>(
    'compliance_requirements',
    sampleRequirements
  );
  const [loading, setLoading] = useState(false);

  const filteredRequirements = dealId 
    ? requirements.filter(req => req.dealId === dealId)
    : requirements;

  const metrics: ComplianceMetrics = {
    total: filteredRequirements.length,
    completed: filteredRequirements.filter(r => r.status === 'completed').length,
    overdue: filteredRequirements.filter(r => 
      r.status !== 'completed' && new Date(r.dueDate) < new Date()
    ).length,
    byCategory: {
      regulatory: filteredRequirements.filter(r => r.category === 'regulatory').length,
      antitrust: filteredRequirements.filter(r => r.category === 'antitrust').length,
      financial: filteredRequirements.filter(r => r.category === 'financial').length,
      legal: filteredRequirements.filter(r => r.category === 'legal').length
    },
    byPriority: {
      high: filteredRequirements.filter(r => r.priority === 'high').length,
      medium: filteredRequirements.filter(r => r.priority === 'medium').length,
      low: filteredRequirements.filter(r => r.priority === 'low').length
    }
  };

  const addRequirement = async (requirement: Omit<ComplianceRequirement, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    try {
      const newRequirement: ComplianceRequirement = {
        ...requirement,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setRequirements([...requirements, newRequirement]);
      return newRequirement;
    } finally {
      setLoading(false);
    }
  };

  const updateRequirement = async (requirementId: string, updates: Partial<ComplianceRequirement>) => {
    setLoading(true);
    try {
      setRequirements(requirements.map(req =>
        req.id === requirementId
          ? { ...req, ...updates, updatedAt: new Date().toISOString() }
          : req
      ));
    } finally {
      setLoading(false);
    }
  };

  const deleteRequirement = async (requirementId: string) => {
    setLoading(true);
    try {
      setRequirements(requirements.filter(req => req.id !== requirementId));
    } finally {
      setLoading(false);
    }
  };

  return {
    requirements: filteredRequirements,
    metrics,
    loading,
    addRequirement,
    updateRequirement,
    deleteRequirement
  };
}